import { getStorage } from '../../../../shared/utils/general.util';
import STORAGE_KEYS from '../../../../shared/constants/storage';
import type { LinkingMethod } from '../../useLinkingWorkflow';

// Ids de variante de la waiting experience. Para agregar una: suma el id aquí
// y registra label + componente en registry.tsx. La rotación por workflowId y
// el tracking la incluyen automáticamente.
export const WAITING_VARIANTS = [
	'banners',
	'quiz',
	'did_you_know',
	'minigame',
	'caza_facturas',
	'slides',
] as const;

export type WaitingVariant = (typeof WAITING_VARIANTS)[number];

export type WaitingVariantResolution = {
	variant: WaitingVariant;
	isNewAssignment: boolean;
};

type WaitingExperienceState = {
	rotationIndex: number;
	assignments: Record<string, WaitingVariant>;
};

const MAX_STORED_ASSIGNMENTS = 12;

const isWaitingVariant = (value: unknown): value is WaitingVariant =>
	WAITING_VARIANTS.includes(value as WaitingVariant);

const readWaitingExperienceState = (): WaitingExperienceState => {
	try {
		const parsed = JSON.parse(
			getStorage().getItem(STORAGE_KEYS.LINKING_WAITING_EXPERIENCE) || '',
		);
		if (
			typeof parsed?.rotationIndex === 'number' &&
			parsed?.assignments &&
			typeof parsed.assignments === 'object'
		) {
			return parsed;
		}
	} catch (error) {}

	return {
		rotationIndex: Math.floor(Math.random() * WAITING_VARIANTS.length),
		assignments: {},
	};
};

export const resolveWaitingVariant = (
	workflowId: string | null,
): WaitingVariantResolution => {
	if (!workflowId) {
		return { variant: 'banners', isNewAssignment: false };
	}

	const state = readWaitingExperienceState();
	const existing = state.assignments[workflowId];
	if (isWaitingVariant(existing)) {
		return { variant: existing, isNewAssignment: false };
	}

	const variant = WAITING_VARIANTS[state.rotationIndex % WAITING_VARIANTS.length];
	const recentAssignments = Object.entries(state.assignments).slice(
		-(MAX_STORED_ASSIGNMENTS - 1),
	);
	const nextState: WaitingExperienceState = {
		rotationIndex: state.rotationIndex + 1,
		assignments: Object.fromEntries([
			...recentAssignments,
			[workflowId, variant],
		]),
	};

	try {
		getStorage().setItem(
			STORAGE_KEYS.LINKING_WAITING_EXPERIENCE,
			JSON.stringify(nextState),
		);
	} catch (error) {}

	return { variant, isNewAssignment: true };
};

export const readLinkingFlowAttempt = (): {
	workflowId: string | null;
	method: LinkingMethod;
} => {
	try {
		const config = JSON.parse(
			getStorage().getItem(STORAGE_KEYS.LINKING_FLOW_CONFIG) || '{}',
		);
		return {
			workflowId: config?.workflowId ?? null,
			method: config?.linkingOptionSelected === 'efirma' ? 'efirma' : 'link',
		};
	} catch (error) {
		return { workflowId: null, method: 'link' };
	}
};

export const shuffleItems = <T,>(items: readonly T[]): T[] => {
	const shuffled = [...items];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
};
